<script setup lang="ts">
import { useProfileProfile } from '@/composables/useProfileProfile';
// import { useAppStore } from '@/stores/appStore';
import { useUserStore } from '@/stores/useUserStore';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import { useToast } from 'primevue/usetoast';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CountrySelect from './CountrySelect.vue';

const emit = defineEmits(['close']);
const { t } = useI18n();
const toast = useToast();
const userStore = useUserStore();
const user = ref({ ...userStore.user });
const { updateUser } = useProfileProfile(toast);
// const appStore = useAppStore();

const languagesList = ref([
  { label: 'Français', value: 'fr' },
  { label: 'English', value: 'en' },
  { label: 'Deutsch', value: 'de' },
  { label: 'Italiano', value: 'it' },
]);

const firstName = ref(user.value?.first_name || '');
const lastName = ref(user.value?.last_name || '');
const phoneNumber = ref(user.value?.phone_number || '');
const oasiNumber = ref(user.value?.oasi_number || '');
const addressLine1 = ref(user.value?.address?.address_line_1 || '');
const addressLine2 = ref(user.value?.address?.address_line_2 || '');
const city = ref(user.value?.address?.city?.name || '');
const postalCode = ref(user.value?.address?.city?.postal_code || ''); // Added postalCode
const selectedLanguage = ref(languagesList.value.find((l) => l.value === user.value?.language) || languagesList.value[0]);
const selectedCountry = ref(user.value?.country_alpha2_code || 'ch');
const isSaving = ref(false);

// const profilePhotoUrl = computed(() => user.value?.profile_photo_url || '')

async function onSave() {
  isSaving.value = true;
  const updates: { [key: string]: unknown } = {
    first_name: firstName.value,
    last_name: lastName.value,
    phone_number: phoneNumber.value,
    oasi_number: oasiNumber.value,
    language: selectedLanguage.value.value as 'fr' | 'en' | 'de' | 'it',
    country_alpha2_code: selectedCountry.value,
    address: {
      address_line_1: addressLine1.value,
      address_line_2: addressLine2.value,
      city: {
        name: city.value,
        postal_code: postalCode.value,
      },
    },
  };
  await updateUser(updates);
  isSaving.value = false;
  emit('close');
}

function onCancel() {
  emit('close');
}
</script>

<template>
  <div class="flex flex-col gap-8 pb-10">
    <!-- Personal Information Section -->
    <section>
      <h3 class="mb-4 text-lg">{{ t('profile.profile.profileTab.personnalData.title') }}</h3>
      <div class="space-y-4">
        <div>
          <h4 class="mb-1 text-sm font-medium">{{ t('profile.profile.profileTab.personnalData.firstName') }}</h4>
          <InputText v-model="firstName" class="w-full" />
        </div>
        <div>
          <h4 class="mb-1 text-sm font-medium">{{ t('profile.profile.profileTab.personnalData.lastName') }}</h4>
          <InputText v-model="lastName" class="w-full" />
        </div>
        <div>
          <h4 class="mb-1 text-sm font-medium">{{ t('auth.user.form.label.phone_number') }}</h4>
          <InputText v-model="phoneNumber" class="w-full" />
        </div>
        <div>
          <h4 class="mb-1 text-sm font-medium">{{ t('auth.user.form.label.oasi_number') }}</h4>
          <InputText v-model="oasiNumber" class="w-full" />
        </div>
      </div>
    </section>

    <!-- Address Section -->
    <section>
      <h3 class="mb-4 text-lg">{{ t('profile.profile.profileTab.personnalData.addressLabel') || 'Address' }}</h3>
      <div class="space-y-4">
        <div>
          <h4 class="mb-1 text-sm font-medium">{{ t('profile.profile.profileTab.personnalData.addressLabel') }}</h4>
          <InputText v-model="addressLine1" class="w-full" :placeholder="t('profile.profile.profileTab.personnalData.addressPlaceholder')" />
        </div>
        <div>
          <h4 class="mb-1 text-sm font-medium">{{ t('profile.profile.profileTab.personnalData.address2Label') || 'Address line 2' }}</h4>
          <InputText v-model="addressLine2" class="w-full" :placeholder="t('profile.profile.profileTab.personnalData.address2Placeholder') || ''" />
        </div>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <h4 class="mb-1 text-sm font-medium">{{ t('profile.profile.profileTab.personnalData.postalCodeLabel') || 'Postal Code' }}</h4>
            <InputText
              v-model="postalCode"
              type="number"
              class="w-full"
              :placeholder="t('profile.profile.profileTab.personnalData.postalCodePlaceholder') || 'e.g. 12345'"
            />
          </div>
          <div>
            <h4 class="mb-1 text-sm font-medium">{{ t('profile.profile.profileTab.personnalData.cityLabel') }}</h4>
            <InputText v-model="city" class="w-full" :placeholder="t('profile.profile.profileTab.personnalData.cityPlaceholder')" />
          </div>
        </div>
        <div>
          <h4 class="mb-1 text-sm font-medium">{{ t('auth.user.form.label.country') }}</h4>
          <CountrySelect v-model="selectedCountry" />
        </div>
      </div>
    </section>

    <!-- Preferences Section -->
    <!-- <section>
      <h3 class="mb-4 text-lg font-semibold">{{ t('profile.profile.profileTab.preferences.title') || 'Preferences' }}
      </h3>
      <div class="space-y-4">
        <div>
          <h4 class="mb-1 text-sm font-medium">{{ t('profile.profile.profileTab.language.title') }}</h4>
          <Dropdown v-model="selectedLanguage" class="w-full md:w-1/2" :options="languagesList" option-label="label">
            <template #value="slotProps">
              {{ slotProps.value.label }}
            </template>
<template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
</Dropdown>
</div>
</div>
</section> -->

    <div class="sticky -bottom-3 z-10 flex justify-end gap-2 border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
      <Button :label="t('common.cancel')" severity="secondary" outlined @click="onCancel" />
      <Button :label="t('common.save')" :loading="isSaving" @click="onSave" />
    </div>
  </div>
</template>
